// Taktisk grafikk: fremrykningsakser (piler) og kontrolltiltak. En operatør
// klikker en rygglinje; verktøyet tegner linja + en fylt pilspiss orientert
// langs siste segment, i venn/fiende-farge. Geometrien er ren og testbar.
import maplibregl, { type Map as MlMap, GeoJSONSource, type LngLat } from 'maplibre-gl';
import bearing from '@turf/bearing';
import { point } from '@turf/helpers';
import { project, featureCollection, type GeoFeature } from '@skade/contracts';
import type { Kartmodus } from '../map/kartmodus.js';
import { hevVerktoylag, naarKartetErKlart } from '../map/verktoylag.js';

export type TacAffiliation = 'friend' | 'hostile';

export const TAC_ID = 'axis';
const TAC_LAG = ['tac-fill', 'tac-line'] as const;

/** Bygger en pilspiss-polygon (fylt trekant) ved [to], pekende bort fra [from].
 * lengthM = hvor langt bak tuppen barbene ligger; spreadDeg = halv åpningsvinkel. */
export function arrowHead(
  from: [number, number],
  to: [number, number],
  lengthM = 300,
  spreadDeg = 22,
): [number, number][] {
  // Peiling fra tupp tilbake mot forrige punkt (retningen barbene ligger i).
  const back = (bearing(point(to), point(from)) + 360) % 360;
  const left = project({ lat: to[1], lon: to[0] }, back - spreadDeg, lengthM);
  const right = project({ lat: to[1], lon: to[0] }, back + spreadDeg, lengthM);
  return [to, [left.lon, left.lat], [right.lon, right.lat], to];
}

/**
 * Full akse-pil av en rygglinje: LineString (ryggen) + Polygon (pilspiss).
 *
 * `akseId` merker BEGGE features med samme id. Uten den kunne aksene bare
 * slettes ALLE samtidig (og bare via Ctrl+K) — funn 8 i UX-rapporten. Med id-en
 * står hver akse som sitt eget objekt i høyrepanelet, med sin egen fjern-vei.
 */
export function buildAxisArrow(
  spine: [number, number][],
  aff: TacAffiliation,
  akseId?: string,
): GeoFeature[] {
  if (spine.length < 2) return [];
  const a = spine[spine.length - 2];
  const b = spine[spine.length - 1];
  // Pilspiss-lengde: 15 % av siste segment (min 120 m), skalerer med tegningen.
  const segM = haversine(a, b);
  const headLen = Math.min(Math.max(segM * 0.3, 120), 1500);
  const head = arrowHead(a, b, headLen);
  return [
    { type: 'Feature', geometry: { type: 'LineString', coordinates: spine }, properties: { aff, akseId } },
    { type: 'Feature', geometry: { type: 'Polygon', coordinates: [head] }, properties: { aff, akseId } },
  ];
}

function haversine(a: [number, number], b: [number, number]): number {
  const R = 6_371_000;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLon = ((b[0] - a[0]) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a[1] * Math.PI) / 180) * Math.cos((b[1] * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export class TacGraphicsTool {
  private active = false;
  private spine: LngLat[] = [];
  private aff: TacAffiliation = 'friend';
  /** Ferdige akser beholdes; live-tegningen slås sammen med dem ved render. */
  private committed: GeoFeature[] = [];
  /** Løpenummer for akse-id. Monotont, så en slettet id ikke gjenbrukes. */
  private akseSeq = 0;

  constructor(private map: MlMap, private modus?: Kartmodus) {
    // ⚠ `map.on('load')` ALENE var feilen bak «Fremrykningsakse tegnet ingenting».
    // Er verktøyet konstruert etter at stilen er lastet, fyrer `load` aldri igjen,
    // kilden `tac` opprettes ikke, og `render()` sin `?.setData` gjør ingenting —
    // helt stille. Se `naarKartetErKlart`.
    naarKartetErKlart(map, () => this.initLag());
    this.modus?.registrer({ id: TAC_ID, navn: 'TEGNER AKSE', stopp: () => this.stop() });
  }

  private initLag() {
    if (this.map.getSource('tac')) return;
    this.map.addSource('tac', { type: 'geojson', data: featureCollection([]) });
    const color: maplibregl.ExpressionSpecification = [
      'match', ['get', 'aff'], 'hostile', '#ff5b5b', /* friend */ '#80e0ff',
    ];
    this.map.addLayer({ id: 'tac-fill', type: 'fill', source: 'tac',
      filter: ['==', '$type', 'Polygon'], paint: { 'fill-color': color, 'fill-opacity': 0.5 } });
    this.map.addLayer({ id: 'tac-line', type: 'line', source: 'tac',
      paint: { 'line-color': color, 'line-width': 3 } });
  }

  setAffiliation(a: TacAffiliation) { this.aff = a; }

  toggle() { this.active ? this.stop() : this.start(); }

  start() {
    if (this.active) return;
    this.modus?.taOver(TAC_ID);
    this.active = true;
    this.spine = [];
    // Uten dette ligger aksen under HV-kommuneflata (42 % rødt fyll) og kan
    // ikke ses, selv om geometrien er helt riktig.
    this.initLag();
    hevVerktoylag(this.map, TAC_LAG);
    this.map.getCanvas().style.cursor = 'crosshair';
    this.map.on('click', this.onClick);
    this.map.on('dblclick', this.onDblClick);
    this.map.on('contextmenu', this.onContext);
    this.map.doubleClickZoom.disable();
    window.addEventListener('keydown', this.onKey);
  }

  stop() {
    if (!this.active) return;
    this.active = false;
    this.spine = [];
    this.map.getCanvas().style.cursor = '';
    this.map.off('click', this.onClick);
    this.map.off('dblclick', this.onDblClick);
    this.map.off('contextmenu', this.onContext);
    this.map.doubleClickZoom.enable();
    window.removeEventListener('keydown', this.onKey);
    this.render();
    this.modus?.frigi(TAC_ID);
  }

  /** Er verktøyet i tegnemodus? (til modus-stripa og test) */
  get erAktiv(): boolean { return this.active; }
  /** Antall festede akser (hver akse = rygg + pilspiss = 2 features). */
  get antallAkser(): number { return this.committed.length / 2; }

  /**
   * Festede akser, én oppføring per akse — til objektlista.
   *
   * Funn 8 i UX-rapporten: aksene kunne bare slettes ALLE samtidig, og bare via
   * Ctrl+K. Nå har hver akse identitet, et representativt punkt og en lengde.
   */
  akser(): Array<{ id: string; aff: TacAffiliation; senter: { lon: number; lat: number }; lengdeM: number }> {
    const ut: Array<{ id: string; aff: TacAffiliation; senter: { lon: number; lat: number }; lengdeM: number }> = [];
    for (const f of this.committed) {
      if (f.geometry.type !== 'LineString') continue;
      const p = (f.properties ?? {}) as { aff?: TacAffiliation; akseId?: string };
      if (!p.akseId) continue; // akse tegnet før id-ene fantes
      const c = f.geometry.coordinates as [number, number][];
      let lengdeM = 0;
      for (let i = 1; i < c.length; i += 1) lengdeM += haversine(c[i - 1], c[i]);
      const spiss = c[c.length - 1];
      ut.push({ id: p.akseId, aff: p.aff ?? 'friend', senter: { lon: spiss[0], lat: spiss[1] }, lengdeM });
    }
    return ut;
  }

  /** Fjern ÉN akse (rygg + pilspiss). Returnerer om noe ble fjernet. */
  fjernAkse(id: string): boolean {
    const før = this.committed.length;
    this.committed = this.committed.filter(
      (f) => ((f.properties ?? {}) as { akseId?: string }).akseId !== id,
    );
    if (this.committed.length === før) return false;
    this.render();
    this.onEndring?.();
    return true;
  }

  /** Meldes ved enhver endring i akse-settet, så objektlista kan tegnes på nytt. */
  onEndring?: () => void;

  clear() {
    this.committed = [];
    this.spine = [];
    this.render();
    this.onEndring?.();
  }

  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); this.stop(); }
    else if (e.key === 'Enter') { e.preventDefault(); this.onFinish(); }
  };
  private onClick = (e: maplibregl.MapMouseEvent) => {
    this.spine.push(e.lngLat);
    this.render();
  };
  private onDblClick = (e: maplibregl.MapMouseEvent) => { e.preventDefault(); this.onFinish(); };
  private onContext = (e: maplibregl.MapMouseEvent) => { e.preventDefault(); this.stop(); };
  private onFinish = () => {
    // Fest den ferdige pila og start en ny (verktøyet blir aktivt).
    if (this.spine.length >= 2) {
      this.akseSeq += 1;
      this.committed.push(...buildAxisArrow(
        this.spine.map((p) => [p.lng, p.lat]), this.aff, `ax${this.akseSeq}`,
      ));
    }
    this.spine = [];
    this.render();
    this.onEndring?.();
  };

  private render() {
    const live = this.spine.length >= 2
      ? buildAxisArrow(this.spine.map((p) => [p.lng, p.lat]), this.aff)
      : [];
    (this.map.getSource('tac') as GeoJSONSource)?.setData(
      featureCollection([...this.committed, ...live]) as never,
    );
  }
}
